import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowBack,
  Restaurant,
  AccessTime,
  CurrencyRupee,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { MenuService } from '../services/menuService.js';
import apiClient, { extractData, handleApiError } from '../services/api.js';

export const MenuManagePage = () => {
  const navigate = useNavigate();
  const { outletId } = useAuth();
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemLoading, setItemLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialog states
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Menu form
  const [menuForm, setMenuForm] = useState({ menu_name: '', is_primary_menu: false });

  // Item form
  const [itemForm, setItemForm] = useState({
    item_name: '',
    price: '',
    image_link: '',
    category1: '',
    category2: '',
    prep_time: '10',
  });

  const fetchMenus = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const data = await MenuService.fetchMenus(outletId);
      setMenus(data);
      if (data.length > 0 && !selectedMenu) {
        setSelectedMenu(data[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [outletId, selectedMenu]);

  const fetchItems = useCallback(async (menuId) => {
    if (!menuId) return;
    setItemLoading(true);
    try {
      const data = await MenuService.fetchMenuItems(menuId);
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setItemLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  useEffect(() => {
    if (selectedMenu) {
      fetchItems(selectedMenu.menu_id);
    }
  }, [selectedMenu, fetchItems]);

  // ═══ Menu CRUD ═══
  const handleSaveMenu = async () => {
    try {
      if (editingMenu) {
        await apiClient.patch(`/menus/${editingMenu.menu_id}`, {
          menu_name: menuForm.menu_name,
          is_primary_menu: menuForm.is_primary_menu,
        });
      } else {
        await apiClient.post('/menus', {
          outlet_id: outletId,
          menu_name: menuForm.menu_name,
          menu_category: 'added',
          is_primary_menu: menuForm.is_primary_menu,
        });
      }
      setMenuDialogOpen(false);
      setEditingMenu(null);
      setMenuForm({ menu_name: '', is_primary_menu: false });
      fetchMenus();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleDeleteMenu = async (menuId) => {
    if (menus.length <= 1) {
      setError('Cannot delete the only menu');
      return;
    }
    try {
      await apiClient.delete(`/menus/${menuId}`);
      if (selectedMenu?.menu_id === menuId) {
        setSelectedMenu(null);
      }
      fetchMenus();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const openEditMenu = (menu) => {
    setEditingMenu(menu);
    setMenuForm({
      menu_name: menu.menu_name,
      is_primary_menu: menu.is_primary_menu === 'yes' || menu.is_primary_menu === true,
    });
    setMenuDialogOpen(true);
  };

  // ═══ Item CRUD ═══
  const handleSaveItem = async () => {
    try {
      const payload = {
        outlet_id: outletId,
        menu_id: selectedMenu.menu_id,
        item_name: itemForm.item_name,
        price: parseFloat(itemForm.price),
        image_link: itemForm.image_link,
        category1: itemForm.category1 ? itemForm.category1.split(',').map((s) => s.trim()) : [],
        category2: itemForm.category2 ? itemForm.category2.split(',').map((s) => s.trim()) : [],
        prep_time: parseInt(itemForm.prep_time) || 10,
      };

      if (editingItem) {
        await apiClient.patch(`/menu-items/${editingItem.Product_ID || editingItem.itemID}`, payload);
      } else {
        await apiClient.post('/menu-items', payload);
      }
      setItemDialogOpen(false);
      setEditingItem(null);
      setItemForm({ item_name: '', price: '', image_link: '', category1: '', category2: '', prep_time: '10' });
      fetchItems(selectedMenu.menu_id);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await apiClient.delete(`/menu-items/${itemId}`);
      fetchItems(selectedMenu.menu_id);
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      item_name: item.name,
      price: String(item.price),
      image_link: item.image || '',
      category1: (item.category || []).join(', '),
      category2: (item.category2 || []).join(', '),
      prep_time: String(item.prep_time || 10),
    });
    setItemDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#EBEBEF', p: 2, gap: 2 }}>
      {/* ─── Left Panel: Menu List ─── */}
      <Box
        sx={{
          width: 280,
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <IconButton onClick={() => navigate('/home')} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight={700} fontSize="1rem">
            Menu Management
          </Typography>
        </Box>

        <Button
          startIcon={<Add />}
          variant="contained"
          size="small"
          onClick={() => {
            setEditingMenu(null);
            setMenuForm({ menu_name: '', is_primary_menu: false });
            setMenuDialogOpen(true);
          }}
          sx={{ mb: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
        >
          Add Menu
        </Button>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {menus.map((menu) => (
            <Box
              key={menu.menu_id}
              onClick={() => setSelectedMenu(menu)}
              sx={{
                p: 1.5,
                mb: 1,
                borderRadius: '12px',
                cursor: 'pointer',
                bgcolor: selectedMenu?.menu_id === menu.menu_id ? 'rgba(69,39,160,0.1)' : 'transparent',
                border: selectedMenu?.menu_id === menu.menu_id ? '1px solid rgba(69,39,160,0.3)' : '1px solid transparent',
                '&:hover': { bgcolor: 'rgba(69,39,160,0.05)' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography fontSize="0.85rem" fontWeight={600}>
                  {menu.menu_name}
                </Typography>
                {(menu.is_primary_menu === 'yes' || menu.is_primary_menu === true) && (
                  <Chip label="Primary" size="small" color="primary" sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }} />
                )}
              </Box>
              <Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditMenu(menu); }}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteMenu(menu.menu_id); }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ─── Right Panel: Menu Items ─── */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 3,
          overflow: 'auto',
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {selectedMenu ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>
                {selectedMenu.menu_name}
              </Typography>
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => {
                  setEditingItem(null);
                  setItemForm({ item_name: '', price: '', image_link: '', category1: '', category2: '', prep_time: '10' });
                  setItemDialogOpen(true);
                }}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
              >
                Add Item
              </Button>
            </Box>

            {itemLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No items in this menu. Click "Add Item" to get started.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {items.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.itemID || item.Product_ID}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
                      {item.image && (
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.name}
                          sx={{ width: '100%', height: 140, objectFit: 'cover' }}
                        />
                      )}
                      <CardContent sx={{ pb: 1 }}>
                        <Typography fontWeight={700} fontSize="0.9rem">
                          {item.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip
                            icon={<CurrencyRupee sx={{ fontSize: 14 }} />}
                            label={item.price}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            icon={<AccessTime sx={{ fontSize: 14 }} />}
                            label={`${item.prep_time || 10} min`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        {(item.category || []).length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {(item.category || []).map((c) => (
                              <Chip key={c} label={c} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 1.5 }}>
                        <Button size="small" startIcon={<Edit />} onClick={() => openEditItem(item)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          color="error"
                          onClick={() => handleDeleteItem(item.Product_ID || item.itemID)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            Select a menu from the left panel, or create a new one.
          </Typography>
        )}
      </Box>

      {/* ═══ Menu Dialog ═══ */}
      <Dialog open={menuDialogOpen} onClose={() => setMenuDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingMenu ? 'Edit Menu' : 'Add Menu'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Menu Name"
            value={menuForm.menu_name}
            onChange={(e) => setMenuForm({ ...menuForm, menu_name: e.target.value })}
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={menuForm.is_primary_menu}
                onChange={(e) => setMenuForm({ ...menuForm, is_primary_menu: e.target.checked })}
              />
            }
            label="Primary Menu (shown to customers)"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMenuDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMenu} disabled={!menuForm.menu_name.trim()}>
            {editingMenu ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Item Dialog ═══ */}
      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Item Name"
              value={itemForm.item_name}
              onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Price"
              type="number"
              value={itemForm.price}
              onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
            <TextField
              fullWidth
              label="Prep Time (minutes)"
              type="number"
              value={itemForm.prep_time}
              onChange={(e) => setItemForm({ ...itemForm, prep_time: e.target.value })}
              InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
            />
            <TextField
              fullWidth
              label="Image URL"
              value={itemForm.image_link}
              onChange={(e) => setItemForm({ ...itemForm, image_link: e.target.value })}
            />
            <TextField
              fullWidth
              label="Categories (comma-separated)"
              value={itemForm.category1}
              onChange={(e) => setItemForm({ ...itemForm, category1: e.target.value })}
              helperText="E.g.: North Indian, Vegetarian"
            />
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={itemForm.category2}
              onChange={(e) => setItemForm({ ...itemForm, category2: e.target.value })}
              helperText="E.g.: Spicy, Bestseller"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveItem}
            disabled={!itemForm.item_name.trim() || !itemForm.price}
          >
            {editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
